import { SubmitHandler } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import CallsFormPhoneField from "./calls-form-phone-field";
import CallsFormSubmit from "./calls-form-submit";
import { useEffect, useState } from "react";
import CallsFormRegionField from "./calls-form-region-field";
import { api } from "../../../../api/Intercepter";
import {
  formatDisplacedPersonLabel,
  type DisplacedPersonInfo,
} from "./displaced-person-label";
import "./calls-card-form.css";

const CallsCardForm = ({ methods, call }) => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  }: any = methods;

  const [hasApp, setHasApp] = useState(false);
  const [governorateIdParams, setGovernorateIdParams] = useState(undefined);
  const [gender, setGender] = useState("");
  const [isExistUser, setIsExistUser] = useState(false);
  const [userId, setUserId] = useState<number>();
  const [isStarred, setIsStarred] = useState(false);
  const [displacedPerson, setDisplacedPerson] =
    useState<DisplacedPersonInfo | null>(null);
  const [autoCompleteData, setAutoCompleteData] = useState<any>({
    neighborhood_id: 0,
    neighborhood_name: "",
    city_id: 0,
    city_name: "",
    governorate_id: 0,
    governorate_name: "",
    sub_city_id: 0,
    sub_city_name: "",
  });

  const displacedPersonLabel = formatDisplacedPersonLabel(displacedPerson);

  const onSubmit: SubmitHandler<any> = async (data) => {
    let cleanedPhoneNumber = data?.phone.replace(
      /^(07|\+96407|96407)/,
      "+9647"
    );
    let cleanedPhoneNumberTo964 = cleanedPhoneNumber.replace(
      /^(\+964|964)/,
      "+964"
    );

    if (isExistUser) {
      await api
        .patch(`${import.meta.env.VITE_SOME_KEY_BASE_URL}/clients/${userId}`, {
          fullName: data.name,
          neighborhoodId: data?.city?.neighborhood_id,
          subCityId: data?.city?.sub_city_id,
          cityId: data?.city?.city_id,
          phoneNumber: String(cleanedPhoneNumberTo964),
          gender: data?.gender,
          notes: data?.info,
        })
        .catch((err) => console.log(err));
    } else {
      await api
        .post(`${import.meta.env.VITE_SOME_KEY_BASE_URL}/clients`, {
          fullName: data.name,
          neighborhoodId: data?.city?.neighborhood_id,
          subCityId: data?.city?.sub_city_id,
          cityId: data?.city?.city_id,
          phoneNumber: String(cleanedPhoneNumberTo964),
          gender: data?.gender,
          notes: data?.info,
        })
        .catch((err) => console.log(err));
    }

    resetForm();
  };

  const triggerAll = () => {
    trigger(["phone", "city", "name", "gender", "info"]);
  };

  const resetForm = () => {
    setValue("phone", "");
    setValue("city", "");
    setValue("name", "");
    setValue("gender", null);
    setValue("info", "");
    setHasApp(false);
    setGovernorateIdParams(undefined);
    setIsExistUser(false);
    setIsStarred(false); // Reset star state
    setDisplacedPerson(null);
    setGender("");
    setAutoCompleteData({
      neighborhood_id: 0,
      neighborhood_name: "",
      city_id: 0,
      city_name: "",
      governorate_id: 0,
      governorate_name: "",
      sub_city_id: 0,
      sub_city_name: "",
    });
  };

  useEffect(() => {
    resetForm();
  }, []);

  const handleSearch = (value) => {
    let cleanedPhoneNumber = value?.replace(/^07/, "7");
    return cleanedPhoneNumber.replace(/^(\+964|964)/, "");
  };

  const getGovernorateByCity = async (cityId) => {
    let response: any = await api.get(
      `${import.meta.env.VITE_SOME_KEY_BASE_URL_LOCATION_MAKER}/city/${cityId}`
    );

    return response?.data;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (call && call[0]?.phone !== getValues("phone")) {
        resetForm();
        setValue("phone", call[0]?.phone);
        api
          .get(
            `${
              import.meta.env.VITE_SOME_KEY_BASE_URL
            }/clients/phone/${handleSearch(
              call[0]?.phone
            )}?relations[]=appClient`
          )
          .then((response) => {
            setIsExistUser(true);
            setHasApp(response?.data?.appClient ?? false);
            setUserId(response?.data?.id);
            setDisplacedPerson(response?.data?.displacedPerson ?? null);

            // Check if client has labels to determine star state
            const hasLabels =
              response?.data?.clientLabels &&
              response?.data?.clientLabels.length > 0;
            setIsStarred(hasLabels);

            // Show star button for all existing users (will be handled in phone field component)
            console.log("Client labels:", response?.data?.clientLabels);
            console.log("Is starred:", hasLabels);

            let location = null;
            try {
              const locationData = response?.data?.location;

              location = locationData[Object.keys(locationData)[0]];

              async function handleSetGovernorate() {
                try {
                  const result = await getGovernorateByCity(
                    response?.data?.cityId
                  );
                  setGovernorateIdParams(result?.governorateId);
                } catch (error) {
                  console.error("Error:", error);
                }
              }

              try {
                handleSetGovernorate();
              } catch (error) {
                console.log(error);
              }
            } catch (error) {}

            setValue("name", response?.data?.fullName ?? "");
            setValue("gender", (response?.data?.gender || null) ?? null);
            setGender(response?.data?.gender || null);
            setValue("info", response?.data?.notes ?? "");
            setValue("city", {
              neighborhood_id: location?.neighborhood_id,
              neighborhood_name: location?.neighborhood_name,
              city_id: location?.city_id,
              city_name: location?.city_name,
              governorate_id: location?.governorate_id,
              governorate_name: location?.governorate_name,
              sub_city_id: location?.sub_city_id,
              sub_city_name: location?.sub_city_name,
            });
            setAutoCompleteData({
              neighborhood_id: location?.neighborhood_id,
              neighborhood_name: location?.neighborhood_name,
              city_id: location?.city_id,
              city_name: location?.city_name,
              governorate_id: location?.governorate_id,
              governorate_name: location?.governorate_name,
              sub_city_id: location?.sub_city_id,
              sub_city_name: location?.sub_city_name,
            });
          })
          .catch(() => {
            setValue("city", "");
            setValue("name", "");
            setValue("gender", null);
            setValue("info", "");
            setIsExistUser(false);
            setIsStarred(false); // Reset star status when user not found
            setDisplacedPerson(null);
          });
      }
    };
    fetchData();
  }, [call]);

  return (
    <Box className="calls-card-form">
      {displacedPersonLabel && (
        <Box className="calls-card-displaced-label" role="status">
          <Typography className="calls-card-displaced-label-text">
            {displacedPersonLabel}
          </Typography>
        </Box>
      )}
      <CallsFormPhoneField
        hasApp={hasApp}
        register={register}
        setValue={setValue}
        gender={gender}
        setGender={setGender}
        errors={errors}
        phoneNumber={getValues("phone")}
        isStarred={isStarred}
        setIsStarred={setIsStarred}
        isExistUser={isExistUser}
        clientId={userId}
      />
      <CallsFormRegionField
        governorateIdParams={governorateIdParams}
        setGovernorateIdParams={setGovernorateIdParams}
        autoCompleteData={autoCompleteData}
        setAutoCompleteData={setAutoCompleteData}
        register={register}
        errors={errors}
        setValue={setValue}
      />
      <CallsFormSubmit
        resetForm={resetForm}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        triggerAll={triggerAll}
      />
    </Box>
  );
};

export default CallsCardForm;
